var _effectorFileName = "/builds/gmini/front-ui-kit/src/Groupings/GroupingRowLayout.tsx";
import { TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import styled from 'styled-components';
import { Typography } from '../Typography';
import { Box } from '../Box';
import { CrossCircle } from '../icons';
import { VirtualizedAutocomplete } from '../VirtualizedAutocomplete';
import { Tooltip } from '../Tooltip';
import { CloseIconWrapper, DeleteIconButton } from './Groupings.styled';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const GroupingRowLayout = ({
  order,
  pending,
  getValue,
  propertyList,
  onListOpen,
  onChange,
  onDelete,
  disabled,
  emptyRows,
  readOnly
}) => {
  const [focusedAutocomplete, setFocusedAutocomplete] = React.useState(false);
  const autocompleteClasses = useAutocompleteStyles({
    rowNumber: order,
    readOnly
  });
  const renderOption = React.useCallback((props, {
    groupName,
    name
  }) => /*#__PURE__*/_jsx(Tooltip, {
    enterDelay: 500,
    enterNextDelay: 500,
    title: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        children: name
      }), /*#__PURE__*/_jsxs(TooltipGroupName, {
        variant: "caption",
        children: ["(", groupName, ")"]
      })]
    }),
    arrow: true,
    placement: "left-start",
    styleContent: {
      width: '100%'
    },
    children: /*#__PURE__*/_jsxs(Root, {
      children: [/*#__PURE__*/_jsx(Name, {
        noWrap: true,
        children: name
      }), /*#__PURE__*/_jsx(GroupName, {
        noWrap: true,
        variant: "caption",
        color: "textSecondary",
        align: "right",
        children: groupName
      })]
    })
  }), []);
  const autocompleteTextFieldClasses = useTextFieldAutocompleteStyles({
    focusedInput: focusedAutocomplete,
    disabled: disabled || pending,
    readOnly
  });
  const onFocusAutocomplete = React.useCallback(() => {
    setFocusedAutocomplete(true);
  }, []);
  const onBlurAutocomplete = React.useCallback(() => {
    setFocusedAutocomplete(false);
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(VirtualizedAutocomplete, {
      size: "small",
      loading: pending,
      clearOnEscape: false,
      fullWidth: true,
      disabled: pending || disabled || readOnly,
      clearIcon: /*#__PURE__*/_jsx("span", {}),
      value: getValue(order),
      id: "grouping-input",
      classes: autocompleteClasses,
      options: propertyList,
      getOptionLabel: option => option.name,
      onChange: (_, value) => {
        setFocusedAutocomplete(false);
        onChange(value, order);
      },
      noOptionsText: pending ? 'Идёт загрузка' : 'Нет доступных свойств',
      renderInput: params => /*#__PURE__*/_jsx(TextField, { ...params,
        placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0443\u0441\u043B\u043E\u0432\u0438\u0435",
        variant: "outlined",
        size: "small",
        classes: autocompleteTextFieldClasses
      }),
      renderOption: renderOption,
      onOpen: onListOpen,
      onFocus: onFocusAutocomplete,
      onBlur: onBlurAutocomplete
    }), /*#__PURE__*/_jsx(CloseIconWrapper, {
      rowNumber: order,
      children: !readOnly && /*#__PURE__*/_jsx(Tooltip, {
        title: disabled || pending || emptyRows ? '' : 'Удалить параметр',
        children: /*#__PURE__*/_jsx(DeleteIconButton, {
          disabled: disabled || pending || emptyRows,
          type: "circle",
          size: "small",
          onClick: e => onDelete(e, order),
          children: /*#__PURE__*/_jsx(CrossCircle, {})
        })
      })
    })]
  });
};
const useAutocompleteStyles = makeStyles(() => ({
  listbox: {
    maxHeight: '75vh'
  },
  root: props => ({
    '& .MuiInputBase-root': {
      cursor: 'default'
    },
    '& .MuiAutocomplete-input': {
      color: '#353B60'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderRadius: '4px',
      transition: 'background-color .2s ease-out, border-color .2s ease-out, box-shadow .2s ease-out'
    },
    '&:not(:first-child)': {
      paddingTop: '11px'
    },
    '& .Mui-disabled': {
      opacity: '0.5'
    }
  }),
  popper: ({
    rowNumber
  }) => ({
    margin: rowNumber && rowNumber > 0 ? '52px 0 0 10px' : '42px 0 0 5px',
    '& .MuiAutocomplete-paper': {
      boxShadow: '0px 5px 10px rgb(53 60 96 / 15%) !important',
      border: '1px solid #EDEFF3 !important',
      borderRadius: '4px',
      margin: '2px -3px 0 0',
      fontSize: '14px',
      fontWeight: 500,
      color: '#353B60'
    },
    '& .MuiAutocomplete-noOptions': {
      color: '#353B60',
      padding: '10px 8px 10px 12px',
      cursor: 'default'
    },
    '& .MuiAutocomplete-popupIndicator': {
      marginRight: '1px'
    },
    '& .MuiAutocomplete-listbox': {
      borderRadius: '4px',
      padding: '0 !important'
    },
    '& .MuiAutocomplete-listbox > li': {
      position: 'relative',
      top: '-8px'
    },
    '& .MuiAutocomplete-option': {
      padding: '10px 8px 10px 12px'
    },
    '& .MuiAutocomplete-option[data-focus="true"]': {
      background: 'rgba(53, 59, 96, 0.05)'
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      background: 'rgba(92, 130, 229, 0.15)'
    },
    '& .MuiAutocomplete-option[data-focus="true"].MuiAutocomplete-option[aria-selected="true"]': {
      background: 'linear-gradient(rgba(53, 59, 96, 0.05), rgba(53, 59, 96, 0.05)), linear-gradient(rgba(92, 130, 229, 0.15), rgba(92, 130, 229, 0.15))'
    }
  })
}));
const TooltipGroupName = styled(Typography)({
  fontStyle: 'italic',
  color: '#ccc'
});
const Root = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%'
});
const Name = styled(Typography)({
  flexGrow: 1,
  fontSize: '14px',
  color: '#353B60',
  fontWeight: 500
});
const GroupName = styled(Typography)({
  fontSize: '13px',
  color: 'rgba(53, 59, 96, 0.4)',
  fontWeight: 'normal'
});
const useTextFieldAutocompleteStyles = makeStyles(() => ({
  root: props => ({
    boxShadow: 'none',
    minHeight: '40px',
    marginLeft: '4px',
    '& .MuiIconButton-label': {
      color: 'rgba(0, 0, 61, 1)'
    },
    '& .MuiAutocomplete-popupIndicator': {
      left: '5px',
      transform: 'unset'
    },
    '& input::placeholder': {
      fontFamily: 'Roboto',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '21px',
      letterSpacing: '0em',
      textAlign: 'left',
      color: 'rgba(53, 59, 96, 0,4)'
    },
    '& .MuiInputBase-root': {
      gap: '8px ',
      padding: '6px 30px 6px 12px !important',
      minHeight: '40px',
      background: props.readOnly ? 'rgba(255, 255, 255, 0.5)' : '#fff',
      border: props.focusedInput ? '1px solid #4C5ECF' : '1px solid transparent',
      transition: 'border-color ease-out .2s',
      boxShadow: props.focusedInput ? '0px 5px 10px rgba(53, 60, 96, 0.1)' : 'unset',
      '&:hover': {
        border: props.focusedInput ? '1px solid #4C5ECF' : '1px solid #C5C6D1',
        borderColor: props.disabled || props.readOnly ? 'transparent' : props.focusedInput ? '#4C5ECF' : '#C5C6D1'
      }
    },
    '& .MuiAutocomplete-endAdornment': {
      display: props.readOnly ? 'none' : 'block'
    },
    '& .Mui-disabled': {
      opacity: props.disabled && !props.readOnly ? 0.5 : 1
    },
    '& input': {
      fontSize: '14px',
      fontWeight: 500,
      color: 'rgba(53, 59, 96, 0.3)',
      padding: '0 !important'
    }
  })
}));